/**
 * StatusBadge Component
 *
 * Displays radio/equipment status with a refined dot + text pattern.
 * Designed to complement enterprise table data without dominating —
 * uses muted, desaturated colours with a subtle background tint
 * rather than fully-saturated filled badges.
 *
 * The visual approach mirrors the StunIndicator pattern: a small
 * coloured dot paired with a lightweight text label inside a
 * softly-tinted container. This reads as confident and professional
 * rather than garish or attention-seeking.
 */

import { Box, Text, type BoxProps } from '@mantine/core';
import { type StatusColour, status as statusColours } from '../tokens/colours';

export type EquipmentStatus =
  | 'online'
  | 'offline'
  | 'stunned'
  | 'warning'
  | 'active'
  | 'available'
  | 'checked-out'
  | 'defective'
  | 'maintenance'
  | 'unknown';

export interface StatusBadgeProps extends Omit<BoxProps, 'color'> {
  /** Status to display */
  status: EquipmentStatus | string;
  /** Optional custom label (defaults to title-cased status) */
  label?: string;
  /**
   * Show pulsing effect — retained for API compatibility.
   * Glow effects have been removed in favour of a cleaner design;
   * this prop is now a no-op.
   * @deprecated No longer produces a visual effect.
   */
  pulse?: boolean;
}

/* ---------------------------------------------------------------
   Human-readable labels for each status value.
   Sentence / title case for a refined, enterprise feel.
   --------------------------------------------------------------- */
const statusLabels: Record<string, string> = {
  online: 'Online',
  offline: 'Offline',
  stunned: 'Stunned',
  warning: 'Warning',
  active: 'Active',
  available: 'Available',
  'checked-out': 'Checked Out',
  defective: 'Defective',
  maintenance: 'Maintenance',
  unknown: 'Unknown',
};

/* ---------------------------------------------------------------
   Desaturated, enterprise-grade colour definitions.

   Each status maps to:
     - dot:  the solid colour for the small indicator dot
     - text: the label colour (same hue, slightly adjusted for readability)
     - bg:   a very low-opacity tint (8%) for the container background

   The colours are deliberately muted — pulled towards neutral tones
   so they sit quietly alongside table data rather than shouting.
   --------------------------------------------------------------- */
interface StatusStyle {
  dot: string;
  text: string;
  bg: string;
}

const statusStyles: Record<string, StatusStyle> = {
  /* Green family — operational / available */
  online: {
    dot: '#059669',
    text: '#047857',
    bg: 'rgba(5, 150, 105, 0.08)',
  },
  available: {
    dot: '#059669',
    text: '#047857',
    bg: 'rgba(5, 150, 105, 0.08)',
  },

  /* Blue family — in use / active */
  active: {
    dot: '#2563eb',
    text: '#1d4ed8',
    bg: 'rgba(37, 99, 235, 0.08)',
  },
  'checked-out': {
    dot: '#2563eb',
    text: '#1d4ed8',
    bg: 'rgba(37, 99, 235, 0.08)',
  },

  /* Red family — faulty / restricted */
  defective: {
    dot: '#dc2626',
    text: '#b91c1c',
    bg: 'rgba(220, 38, 38, 0.08)',
  },
  stunned: {
    dot: '#dc2626',
    text: '#b91c1c',
    bg: 'rgba(220, 38, 38, 0.08)',
  },

  /* Amber family — attention / service */
  warning: {
    dot: '#d97706',
    text: '#b45309',
    bg: 'rgba(217, 119, 6, 0.08)',
  },
  maintenance: {
    dot: '#d97706',
    text: '#b45309',
    bg: 'rgba(217, 119, 6, 0.08)',
  },

  /* Grey family — inactive / unknown */
  offline: {
    dot: '#6b7280',
    text: '#6b7280',
    bg: 'rgba(107, 114, 128, 0.08)',
  },
  unknown: {
    dot: '#6b7280',
    text: '#6b7280',
    bg: 'rgba(107, 114, 128, 0.08)',
  },
};

/** Fallback style for any unrecognised status value. */
const fallbackStyle: StatusStyle = {
  dot: '#6b7280',
  text: '#6b7280',
  bg: 'rgba(107, 114, 128, 0.08)',
};

/**
 * Resolve the visual style for a given status string.
 */
function getStyle(status: string): StatusStyle {
  return statusStyles[status] ?? fallbackStyle;
}

/**
 * StatusBadge - Displays equipment/radio status with refined styling
 *
 * Uses a small coloured dot alongside a lightweight text label
 * inside a subtly-tinted container. The design is intentionally
 * quiet: it communicates status clearly without competing with
 * the surrounding data for attention.
 *
 * @example
 * ```tsx
 * <StatusBadge status="online" />
 * <StatusBadge status="checked-out" label="In Use" />
 * <StatusBadge status="defective" />
 * ```
 */
export function StatusBadge({
  status,
  label,
  pulse: _pulse,
  style,
  ...props
}: StatusBadgeProps) {
  const colours = getStyle(status);
  const displayLabel = label ?? statusLabels[status] ?? status;

  return (
    <Box
      data-testid="status-badge"
      data-status={status}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px 3px 8px',
        borderRadius: 6,
        backgroundColor: colours.bg,
        /* Smooth transition for any dynamic status changes */
        transition: 'background-color 200ms ease, color 200ms ease',
        ...style,
      }}
      {...props}
    >
      {/* Status indicator dot — 6px solid circle */}
      <Box
        style={{
          width: 6,
          height: 6,
          minWidth: 6,
          borderRadius: '50%',
          backgroundColor: colours.dot,
          transition: 'background-color 200ms ease',
        }}
      />

      {/* Status label — lightweight, muted, sentence case */}
      <Text
        component="span"
        size="xs"
        fw={500}
        style={{
          color: colours.text,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          transition: 'color 200ms ease',
        }}
      >
        {displayLabel}
      </Text>
    </Box>
  );
}

/**
 * Get the raw colour value for a status (for custom styling).
 *
 * Returns the dot colour from the refined palette, falling back
 * to the original token colour for statuses defined in the
 * colour tokens file.
 */
export function getStatusColourValue(status: EquipmentStatus | string): string {
  const style = statusStyles[status];
  if (style) return style.dot;
  return statusColours[status as StatusColour] ?? fallbackStyle.dot;
}
